import * as React from 'react';
import { AlertTriangle, ArrowRight, Package, Search, Warehouse } from 'lucide-react';
import type { CorredorResumo } from '@/lib/tipos';
import { MATIZ_VAZIO, SATURACAO_VAZIO } from '@/lib/semaforo';
import { useTema } from '@/hooks/useTema';
import { cn, compararNatural, numero, plural } from '@/lib/utils';
import { CampoComIcone } from '@/componentes/ui/primitivos';
import { Segmentado } from '@/componentes/ui/segmentado';

/**
 * Planta do CD (zoom afastado): cada RUA é um cartão com nome, ocupação,
 * esquema do rack (lotes × andares) e heat por banda de semáforo. Clicar abre
 * a elevação da rua.
 *
 * É HTML de propósito: navega por teclado e o leitor de tela lê os números.
 *
 * Iteração 3: além do acabamento, ganhou barra de controle — busca de rua,
 * ordenação e resumo do CD. Antes a única ordem possível era "mais cheia
 * primeiro"; com 40+ ruas isso obriga a varrer a tela para achar uma rua
 * específica.
 */

type Ordem = 'cheias' | 'vazias' | 'nome';

/**
 * Cor por banda de semáforo (as MESMAS do rack, para consistência):
 * vazio=azul claro, até 40% verde, 40–75% âmbar, acima 75% vermelho.
 *
 * A rua vazia toma o matiz de `lib/semaforo`, e não um valor próprio: aqui a
 * cor é montada com `hsl()` a partir do percentual, então não dá para
 * consumir o hex da tabela — mas as duas têm de concordar, senão a mesma rua
 * aparece azul num nível de zoom e cinza no outro.
 */
function heat(pct: number, escuro: boolean): { barra: string; fundo: string; borda: string } {
  const p = Math.max(0, Math.min(100, pct));
  const base = p === 0 ? MATIZ_VAZIO : p < 40 ? 145 : p < 75 ? 38 : 2; // azul, verde, âmbar, vermelho
  const sat = p === 0 ? SATURACAO_VAZIO : 70;
  return {
    barra: `hsl(${base} ${sat}% ${escuro ? 55 : 45}%)`,
    fundo: escuro ? `hsl(${base} ${sat}% 14%)` : `hsl(${base} ${Math.min(72, sat + 5)}% 97%)`,
    borda: escuro ? `hsl(${base} ${sat}% 24%)` : `hsl(${base} 45% 86%)`,
  };
}

export function PlantaCorredores({
  corredores,
  aoAbrirCorredor,
  realceSku,
  vagasPorRua,
}: {
  corredores: readonly CorredorResumo[];
  aoAbrirCorredor: (corredor: string) => void;
  /** SKU buscado: as ruas que o contêm são destacadas e vêm primeiro. */
  realceSku?: number | null | undefined;
  /** Rua (normalizada) → nº de vagas com o SKU buscado. */
  vagasPorRua?: Map<string, number> | undefined;
}): JSX.Element {
  const { escuro } = useTema();
  const [ordem, definirOrdem] = React.useState<Ordem>('cheias');
  const [filtro, definirFiltro] = React.useState('');

  const buscandoSku = realceSku != null && (vagasPorRua?.size ?? 0) > 0;

  const totais = React.useMemo(() => {
    const posicoes = corredores.reduce((s, c) => s + c.posicoes, 0);
    const ocupadas = corredores.reduce((s, c) => s + c.ocupadas, 0);
    return {
      ruas: corredores.length,
      posicoes,
      ocupadas,
      pct: posicoes > 0 ? Math.round((ocupadas / posicoes) * 100) : 0,
      cheias: corredores.filter((c) => c.percentual >= 90).length,
    };
  }, [corredores]);

  const visiveis = React.useMemo(() => {
    const t = filtro.trim().toLowerCase();
    const base = t ? corredores.filter((c) => c.corredor.toLowerCase().includes(t)) : corredores;
    return [...base].sort((a, b) => {
      // Com SKU buscado, as ruas que têm o produto vêm sempre primeiro.
      if (buscandoSku) {
        const va = vagasPorRua?.get(a.corredor.toUpperCase()) ?? 0;
        const vb = vagasPorRua?.get(b.corredor.toUpperCase()) ?? 0;
        if (va !== vb) return vb - va;
      }
      if (ordem === 'nome') return compararNatural(a.corredor, b.corredor);
      if (ordem === 'vazias')
        return a.percentual - b.percentual || compararNatural(a.corredor, b.corredor);
      return b.percentual - a.percentual || compararNatural(a.corredor, b.corredor);
    });
  }, [corredores, filtro, ordem, buscandoSku, vagasPorRua]);

  const nRuasComSku = buscandoSku
    ? [...(vagasPorRua?.values() ?? [])].filter((n) => n > 0).length
    : 0;

  return (
    <div className="flex h-full min-h-0 flex-col">
      {/* Barra de controle da planta */}
      <div className="flex flex-wrap items-center gap-2.5 border-b border-borda px-4 py-3">
        <ResumoCD totais={totais} />
        <div className="ml-auto flex flex-wrap items-center gap-2">
          <CampoComIcone
            icone={<Search className="h-4 w-4" />}
            value={filtro}
            onChange={(e) => definirFiltro(e.target.value)}
            placeholder="Filtrar rua…"
            aria-label="Filtrar rua"
            className="w-[168px]"
            classeCampo="min-h-[38px] text-[13px]"
          />
          <Segmentado
            valor={ordem}
            aoMudar={definirOrdem}
            rotuloAcessivel="Ordenar ruas"
            tamanho="pequeno"
            opcoes={[
              { valor: 'cheias', rotulo: 'Mais cheias' },
              { valor: 'vazias', rotulo: 'Mais vazias' },
              { valor: 'nome', rotulo: 'A–Z' },
            ]}
          />
        </div>
      </div>

      {buscandoSku ? (
        <div className="mx-4 mt-3 flex items-center gap-2.5 rounded-xl border border-primaria/25 bg-primaria-suave px-3.5 py-2.5 text-[13px]">
          <span className="grid h-8 w-8 shrink-0 place-items-center rounded-lg bg-primaria text-white">
            <Package className="h-4 w-4" />
          </span>
          <span>
            SKU <b className="font-mono">{realceSku}</b> está em{' '}
            <b>
              {numero(nRuasComSku)} {plural(nRuasComSku, 'rua', 'ruas')}
            </b>{' '}
            — destacadas abaixo. Clique numa para ver as vagas.
          </span>
        </div>
      ) : null}

      <div className="min-h-0 flex-1 overflow-y-auto p-4">
        {visiveis.length === 0 ? (
          <p className="py-16 text-center text-sm text-textoFraco">
            Nenhuma rua com “{filtro}”.
          </p>
        ) : (
          <ul className="grid grid-cols-[repeat(auto-fill,minmax(206px,1fr))] gap-3">
            {visiveis.map((c) => {
              const cor = heat(c.percentual, escuro);
              const vagasSku = vagasPorRua?.get(c.corredor.toUpperCase()) ?? 0;
              const temSku = buscandoSku && vagasSku > 0;
              const apagado = buscandoSku && vagasSku === 0;
              return (
                <li key={c.corredor}>
                  <button
                    type="button"
                    onClick={() => aoAbrirCorredor(c.corredor)}
                    className={cn(
                      'group relative flex w-full flex-col gap-3 overflow-hidden rounded-xl border p-3.5 text-left shadow-xs',
                      'transition-all duration-200 hover:-translate-y-1 hover:shadow-media',
                    )}
                    style={{
                      background: cor.fundo,
                      borderColor: temSku ? 'rgb(var(--cor-primaria))' : cor.borda,
                      boxShadow: temSku ? 'var(--sombra-marca)' : undefined,
                      opacity: apagado ? 0.38 : 1,
                    }}
                  >
                    {/* Faixa de calor no topo do cartão */}
                    <span
                      aria-hidden
                      className="absolute inset-x-0 top-0 h-1"
                      style={{ background: cor.barra }}
                    />

                    <div className="flex items-center justify-between gap-2">
                      <span className="font-mono text-[15px] font-bold tracking-tight text-texto">
                        {c.corredor}
                      </span>
                      {temSku ? (
                        <span className="tabular rounded-full bg-primaria px-2 py-0.5 text-[10.5px] font-bold text-white">
                          {numero(vagasSku)} {plural(vagasSku, 'vaga', 'vagas')}
                        </span>
                      ) : c.percentual >= 90 ? (
                        <span className="inline-flex items-center gap-1 rounded-full bg-status-atencaoFundo px-2 py-0.5 text-[10.5px] font-bold text-status-atencao">
                          <AlertTriangle className="h-3 w-3" />
                          cheia
                        </span>
                      ) : (
                        <ArrowRight className="h-4 w-4 -translate-x-1 text-textoSuave opacity-0 transition-all group-hover:translate-x-0 group-hover:opacity-100" />
                      )}
                    </div>

                    <div className="flex items-end justify-between gap-2">
                      <span
                        className="tabular text-[30px] font-bold leading-none tracking-[-0.03em]"
                        style={{ color: cor.barra }}
                      >
                        {c.percentual}
                        <span className="text-[15px] font-semibold">%</span>
                      </span>
                      <EsquemaRack
                        lotes={c.modulos}
                        andares={c.niveis}
                        cor={cor.barra}
                        pct={c.percentual}
                      />
                    </div>

                    <div className="h-1.5 overflow-hidden rounded-full bg-black/[0.06] dark:bg-white/10">
                      <span
                        className="block h-full rounded-full transition-[width] duration-500"
                        style={{
                          width: `${Math.max(3, c.percentual)}%`,
                          background: cor.barra,
                        }}
                      />
                    </div>

                    <div className="flex items-center justify-between text-[11px] text-textoFraco">
                      <span className="tabular font-medium">
                        {numero(c.ocupadas)}/{numero(c.posicoes)} vagas
                      </span>
                      <span className="tabular">
                        {numero(c.modulos)} × {numero(c.niveis)}
                      </span>
                    </div>
                  </button>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </div>
  );
}

/**
 * Esquema do rack em miniatura: a SILHUETA real da rua (nº de lotes × nº de
 * andares), preenchida na proporção da ocupação. Não inventa posição: diz
 * quanto da rua está cheia, no formato da rua.
 */
function EsquemaRack({
  lotes,
  andares,
  cor,
  pct,
}: {
  lotes: number;
  andares: number;
  cor: string;
  pct: number;
}): JSX.Element {
  const colunas = Math.min(14, Math.max(3, lotes));
  const linhas = Math.min(7, Math.max(2, andares));
  const total = colunas * linhas;
  const cheias = Math.round((pct / 100) * total);
  return (
    <span
      aria-hidden
      title={`${lotes} lotes × ${andares} andares`}
      className="grid shrink-0 gap-[1.5px] opacity-90"
      style={{ gridTemplateColumns: `repeat(${colunas}, 3px)` }}
    >
      {Array.from({ length: total }, (_, i) => (
        <span
          key={i}
          className="h-[3px] rounded-[1px]"
          style={{
            // Preenche de baixo para cima, como o rack enche na vida real.
            background: total - 1 - i < cheias ? cor : 'currentColor',
            opacity: total - 1 - i < cheias ? 1 : 0.16,
          }}
        />
      ))}
    </span>
  );
}

function ResumoCD({
  totais,
}: {
  totais: { ruas: number; posicoes: number; ocupadas: number; pct: number; cheias: number };
}): JSX.Element {
  return (
    <div className="flex flex-wrap items-center gap-x-4 gap-y-1">
      <span className="flex items-center gap-2">
        <span className="grid h-9 w-9 place-items-center rounded-lg bg-primaria-suave text-primaria">
          <Warehouse className="h-4 w-4" />
        </span>
        <span className="leading-tight">
          <span className="tabular block text-[15px] font-bold">{numero(totais.ruas)} ruas</span>
          <span className="block text-[11px] text-textoFraco">planta do CD</span>
        </span>
      </span>
      <span className="h-8 w-px bg-borda" />
      <DadoResumo valor={`${totais.pct}%`} rotulo="ocupação geral" destaque />
      <DadoResumo valor={numero(totais.posicoes)} rotulo="vagas mapeadas" />
      {totais.cheias > 0 ? (
        <DadoResumo valor={numero(totais.cheias)} rotulo="ruas acima de 90%" alerta />
      ) : null}
    </div>
  );
}

function DadoResumo({
  valor,
  rotulo,
  destaque = false,
  alerta = false,
}: {
  valor: string;
  rotulo: string;
  destaque?: boolean;
  alerta?: boolean;
}): JSX.Element {
  return (
    <span className="leading-tight">
      <span
        className={cn(
          'tabular block text-[15px] font-bold',
          destaque && 'text-primaria',
          alerta && 'text-status-atencao',
        )}
      >
        {valor}
      </span>
      <span className="block text-[11px] text-textoFraco">{rotulo}</span>
    </span>
  );
}
